import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import ArrowDown from '@/assets/svgs/arrow-down.icon.svg?react';
import CarofiLogo from '@/assets/svgs/carofi-logo.icon.svg?react';
import ImageWithAuthorization from '@/shared/components/core-ui/image-with-authorization/image-with-authorization.component';
import { LanguageSwitcher } from '@/shared/components/core-ui/language-switcher';
import { NotificationBell } from '@/shared/components/group-ui/notification-bell/notification-bell.component';
import BugReporterWidget from '@/shared/components/widgets/BugReporter';
import { SERVER_BASE_URL } from '@/shared/config/routes-config';
import { useLocalization } from '@/shared/hooks/useLocalization';
import { RootState } from '@/shared/redux/reducers';

import { AppBarHeader } from './global-header.styles';

const mobileOnlyElement = {
  display: { xs: 'flex', md: 'none' }
};

const webOnlyElement = {
  display: { xs: 'none', md: 'flex' }
};

export interface GlobalHeaderProps {
  eventHandlers: {
    logoutHandler?: () => void;
    toggleMobileMenu: () => void;
  };
}

/**
 * @param logoutHandler to handle when user logout
 * @param toggleMobileMenu state to handle hide/show side menu in mobile view
 */

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({ eventHandlers }) => {
  const { logoutHandler, toggleMobileMenu } = eventHandlers;
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const { isRTLDirect: isRTL, t } = useLocalization();
  const displayName = useSelector((state: RootState) => state.auth.name);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBarHeader position="fixed">
      <Toolbar disableGutters sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <Box sx={{ ...mobileOnlyElement, flexGrow: { xs: 1, md: 0 } }}>
          <IconButton aria-label="openDrawer" onClick={toggleMobileMenu}>
            <MenuIcon color="primary" fontSize="large" />
          </IconButton>
        </Box>

        <Box aria-label="logo" sx={{ flexGrow: { xs: 1, md: 0 }, mt: 1.375 }}>
          <CarofiLogo style={{ width: '120px', height: '35px' }} />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ ...webOnlyElement, flexGrow: 1, gap: 0.5 }}
        >
          <BugReporterWidget />
          <NotificationBell />
          <LanguageSwitcher />

          <ImageWithAuthorization
            data={{
              currentUserImage: true,
              alt: 'User Profile'
            }}
            config={{
              width: 40,
              height: 40,
              objectFit: 'cover',
              borderRadius: '50%',
              showLoader: true
            }}
          />

          <Typography aria-label="userName" variant="h5" color="textPrimary">
            {displayName || t('SHARED.HEADER.NO_NAME_AVAILABLE')}
          </Typography>
          <IconButton aria-label="openUserMenu_Web" onClick={handleOpenUserMenu}>
            <ArrowDown
              style={{
                marginLeft: isRTL ? 0 : theme.spacing(0.5),
                marginRight: isRTL ? theme.spacing(0.5) : 0
              }}
            />
          </IconButton>
        </Stack>

        <Box sx={{ flexGrow: 0, ...mobileOnlyElement }}>
          <LanguageSwitcher />
          <IconButton
            aria-label="openUserMenu_Mob"
            onClick={handleOpenUserMenu}
            sx={{ p: 0, ml: isRTL ? 0 : 1, mr: isRTL ? 1 : 0 }}
          >
            <ImageWithAuthorization
              aria-label="avatar_Mob"
              data={{
                currentUserImage: true,
                alt: 'User Profile'
              }}
              config={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: '50%',
                showLoader: true
              }}
            />
          </IconButton>

          <Menu
            sx={{ mt: theme.spacing(5.75) }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: isRTL ? 'left' : 'right'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: isRTL ? 'left' : 'right'
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <MenuItem key="Notifications" sx={mobileOnlyElement} onClick={handleCloseUserMenu}>
              <Typography aria-label="menuNotifications" variant="h6" component="div">
                {t('SHARED.HEADER.MENU_ITEMS.NOTIFICATIONS')}
              </Typography>
            </MenuItem>
            <Divider sx={mobileOnlyElement} />
            <MenuItem key="logout" onClick={logoutHandler}>
              <Typography aria-label="logout" variant="h6" component="div">
                {t('SHARED.HEADER.MENU_ITEMS.LOGOUT')}
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBarHeader>
  );
};

export default GlobalHeader;
